<template>
    <div class="project-report flex h-full flex-col bg-gray-10">

        <div class="z-10 flex h-[60px] items-center border-b border-gray-40 bg-white px-6 py-2.5
        ">
            <div class="mr-6 flex h-6 shrink-0 cursor-pointer items-center text-sm leading-6 text-black"
                 @click="handleBack">
                <PtIcon icon="SolidArrowLeft" class="mr-1 fill-grey-450 text-2xl" />{{ t('report.back') }}
            </div>
            <div class="mr-3 hidden h-8 w-8 shrink-0 lg:block">
                <ProjectIcon :icon="project?.icon || 1" size="sm" />
            </div>
            <div class="line-clamp-1 grow text-xl font-semibold leading-7 text-black" :title="project?.title">
                {{ project?.title }}
            </div>
            <div class="ml-4 flex shrink-0 items-center gap-2">
                <span class="hidden text-xs text-gray-90 lg:block">{{ t('report.lastUpdate') }} {{
                    projectUpdateTime }}</span>
                <EditionUsage :usedWhere="UsageAmountWhere.TECH_RESEARCH">
                    <button class="th-btn th-btn-outline" data-size="md" @click="handleRefresh"
                            :disabled="!canExport || isRefreshLoading">
                        <span v-if="isRefreshLoading"
                              class="th-btn-icon th-loading th-loading-spinner th-loading-sm"></span>
                        <PtIcon v-else class="th-btn-icon text-2xl" icon="SolidRefresh" />
                        <span>{{ t('report.update') }}</span>
                    </button>
                </EditionUsage>
                <a v-if="canDownload && downloadUrl" :href="downloadUrl" class="th-btn hidden lg:flex">{{
                    t('report.readyToDownload') }}</a>
                <button v-else-if="canDownload" class="th-btn hidden lg:flex" data-size="md" @click="handleDownload"
                        :disabled="!canExport || isExportLoading || !canDownload">
                    <span v-if="isExportLoading" class="th-btn-icon th-loading th-loading-spinner th-loading-sm"></span>
                    <PtIcon v-else class="th-btn-icon text-2xl" icon="DownloadFile" />
                    <span>{{ t('report.download') }}</span>
                </button>
            </div>
        </div>
        <div class="project-report-body flex flex-1 justify-center gap-6 overflow-y-auto px-6">

            <div class="report-card-content flex flex-1 flex-col gap-6 py-6 pb-[60px]">
                <template v-for="(item, index) in directoryList" :key="index">
                    <component :id="project?.id" :task="item" :is="getComponent(item.module_type)"
                               :class="generateCardClass(item)" :ref="el => componentRefs[index] = el">
                    </component>
                </template>

                <div class="mt-[180px] flex flex-col items-center justify-center gap-5 pb-[60px]">
                    <PtIcon icon="End" class="text-[100px]"></PtIcon>
                    <div class="text-sm text-gray-90">{{ t('report.toTheEnd') }}</div>
                </div>
            </div>

            <RightAside :catalogList="directoryList" :loading="loadingDirectory" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { PtIcon } from 'patsnap-biz'
import { computed, onMounted, ref, defineAsyncComponent, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ProjectIcon from '@/components/ProjectIcon.vue';
import { useReport } from '@/composables/use-report';
import { useGlobalState } from '@/composables/use-global-store';
import { useI18n } from 'vue-i18n';
import { formatDate } from '@eureka/utils/date-format'
import { controlDeferred } from '@eureka/utils/control';
import { useDirectory } from '@/composables/use-directory';
import { REPORT_TEMPLATE, type CatalogItem } from '@/types';
import TechnicalBackground from './template/TechBg.vue';
import RightAside from '@/components/report/RightAside.vue';
import EditionUsage from '@eureka/edition-upgrade/src/components/edition/EditionUsage.vue';
import { UsageAmountWhere } from '@eureka/edition-upgrade/src/constants';

import '@patsnap-ui/icon/assets/downloadFile.svg';
import '@patsnap-ui/icon/assets/solid/ArrowLeft.svg';
import '@patsnap-ui/icon/assets/solid/refresh.svg';
import '@/assets/icons/end.svg';

const { directoryList, loadingDirectory } = useDirectory()

const canDownload = ref(false)

const router = useRouter();

const route = useRoute()

const { t, locale } = useI18n()

const { project } = useGlobalState();
const {
    canExport,
    isExportLoading,
    isRefreshLoading,
    downloadUrl,
    downloadReport,
    updateCardInstances,
    refreshReports,
} = useReport();

const projectUpdateTime = computed(() => {
    return formatDate({ date: project.value?.lastUpdate as string, currentLang: locale.value as 'cn' | 'en' | 'jp' | 'tw' })
})

const componentMap = {
    [REPORT_TEMPLATE.TECHNICAL_BACKGROUND]: TechnicalBackground,
    [REPORT_TEMPLATE.TECHNOLOGY_STATUS_CHALLENGES]: defineAsyncComponent(() => import('./template/TechChallenge.vue')),
    [REPORT_TEMPLATE.MARKET_ANALYSIS]: defineAsyncComponent(() => import('./template/MarketAnalysis.vue')),
    [REPORT_TEMPLATE.TECHNOLOGICAL_EVOLUTION_PATH]: defineAsyncComponent(() => import('./template/TechRoadmap.vue')),
    [REPORT_TEMPLATE.HEAD_PLAYER_ANALYSIS]: defineAsyncComponent(() => import('./template/HeadPlayer.vue')),
    [REPORT_TEMPLATE.TECHNICAL_SCHEME_REVIEW]: defineAsyncComponent(() => import('./template/TechScheme.vue')),
    [REPORT_TEMPLATE.KEY_PATENT_INTERPRETATION]: defineAsyncComponent(() => import('./template/CoreInnovation.vue')),
    [REPORT_TEMPLATE.IDEA_SUMMARY]: defineAsyncComponent(() => import('./template/IdeaSummary.vue')),
    [REPORT_TEMPLATE.CUSTOM_MODULE]: defineAsyncComponent(() => import('./template/Custom.vue')),
}

const componentRefs = ref<any[]>([]);

function getComponent(i: REPORT_TEMPLATE) {
    return componentMap[i]
}

watch(() => componentRefs.value, (refs) => {
    updateCardInstances(refs);
}, {
    immediate: true,
});

onMounted(async() => {
    /**
     * 用户是否可以下载报告
     */
    const { download } = await controlDeferred.promise;
    canDownload.value = download;
});

function generateCardClass(item: CatalogItem) {
    return ['report-card-template__' + item.module_type, 'report-card-task__' + item.task_group_type]
}

function handleBack() {
    const { projectId } = route.params;
    router.push({
        name: 'chatDetail',
        params: {
            projectId,
        },
    })
}

function handleRefresh() {
    refreshReports(!project.value?.project_version)
}

function handleDownload() {
    downloadReport();
}

</script>
